import { Component, type ErrorInfo, type ReactNode } from 'react';
import { QrCode } from 'lucide-react';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

interface ShareQrErrorBoundaryProps {
  children: ReactNode;
  /**
   * Rendered instead of the built-in fallback below when provided. Callers whose QR sits
   * in a different footprint or needs different wording (e.g. the profile QR dialog, which
   * has no Copy-link row) pass their own; omit it to get the share-sheet-shaped default
   * unchanged.
   */
  fallback?: ReactNode;
}

interface ShareQrErrorBoundaryState {
  hasError: boolean;
}

/**
 * A boundary the size of the QR, not the size of the app.
 *
 * Both `SocialShareSheet`'s `ShareQrPanel` and the profile page's `ProfileQrDialogContent`
 * are lazily imported, and a `React.lazy` rejection — the ordinary shape of a post-deploy
 * chunk 404, where a stale index.html asks for a hashed file the new deploy no longer
 * serves — is not something `installChunkErrorHandlers`' window listeners ever see. Left
 * unguarded it reaches the app-root `ErrorBoundary`, which answers a chunk error with a
 * full-screen "Updating to latest version..." and a hard reload: the sheet or profile page,
 * and whatever the user was mid-share of, gone over one optional graphic.
 *
 * Caught here instead, everything else around the QR — Copy link and the social buttons in
 * the share sheet, the rest of the profile page in the dialog case — keeps working. Pass
 * `fallback` to size and word the caught state for the caller; the default below is the
 * share sheet's. Deliberately no retry in either case: a 404'd chunk stays 404 until the
 * page reloads, and the house retry (`handleChunkLoadError`) IS that reload, which is the
 * outcome being avoided.
 */
export class ShareQrErrorBoundary extends Component<
  ShareQrErrorBoundaryProps,
  ShareQrErrorBoundaryState
> {
  public state: ShareQrErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ShareQrErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('QR panel failed to load', {
      ...sanitizeError(error),
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      // Same footprint as the panel and its Suspense skeleton — the QR square plus an h-11
      // row where the Download button sits. This renders inline in an already-visible
      // header, so a shorter fallback would shove everything below it, including the button
      // the user's finger is on, at the exact moment the QR failed.
      <div className="mb-2 flex flex-col items-center gap-2.5 text-center">
        <div className="flex aspect-square w-48 items-center justify-center">
          <QrCode className="size-10 text-gray-300" />
        </div>
        <p className="flex min-h-11 w-48 items-center justify-center font-fira text-sm text-[#646464]">
          Couldn't load the QR. Copy the link instead.
        </p>
      </div>
    );
  }
}
